import React, { useContext, useEffect, useState } from "react";
import { IoSearchSharp } from "react-icons/io5";
import { useNavigate, useSearchParams } from "react-router-dom";

import { AuthContext } from "../../context/AuthContext";
import { findClaimCompany } from "../../services/helpers/AccountClaimHelpers";
import { DEFAULT_DASHBOARD_TIME_RANGE } from "../../services/helpers/DashboardHelpers";
import { getSearchParamNumber } from "../../services/nav/QueryStringHelpers";
import { IPropertyClaims } from "../../services/zeos-api/contracts/Account";
import { PropertyDropdown } from "./PropertyDropdown";
import { DashboardTimeRangePills } from "./shared/DashboardTimeRangePills";

interface IProps {
  propertyCategoryName?: string;
  showCategory?: boolean;
  tailLabel?: string;
  timeRange?: string;
  onTimeRangeChange?: (value: string) => void;
  unavailableTimeRanges?: string[] | null;
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
}

export const DashboardPageHeader = (props: IProps) => {
  const {
    propertyCategoryName,
    showCategory,
    tailLabel,
    timeRange = DEFAULT_DASHBOARD_TIME_RANGE,
    onTimeRangeChange,
    unavailableTimeRanges,
    searchQuery = "",
    onSearchChange,
  } = props;
  const auth = useContext(AuthContext);
  const navigate = useNavigate();

  const [properties, setProperties] = useState<IPropertyClaims[]>([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState<
    number | undefined
  >(undefined);

  const [params] = useSearchParams();
  const companyId = getSearchParamNumber("company", params);
  const urlPropertyId = getSearchParamNumber("property", params);

  useEffect(() => {
    const initAsync = async () => {
      const user = await auth.zeosUserAsync();
      const company = findClaimCompany(
        user.customerClaims,
        companyId,
        urlPropertyId
      );
      setProperties(company?.properties ?? []);
    };
    initAsync();
  }, [auth, companyId, urlPropertyId]);

  //keep selection in sync with URL
  useEffect(() => {
    if (urlPropertyId != null) {
      setSelectedPropertyId(urlPropertyId);
    } else if (properties.length > 0) {
      setSelectedPropertyId(properties[0].propertyId);
    } else {
      setSelectedPropertyId(undefined);
    }
  }, [properties, urlPropertyId]);

  // When a property is picked we update the state AND the URL so the rest of the page updates
  const handlePropertySelect = (pid: number) => {
    setSelectedPropertyId(pid);

    const next = new URLSearchParams(params);
    if (companyId != null) next.set("company", String(companyId));
    next.set("property", String(pid));

    navigate({ search: `?${next.toString()}` }, { replace: false });
  };

  return (
    <div className="flex flex-wrap justify-between gap-5 mx-4 py-4 ">
      <PropertyDropdown
        properties={properties}
        label="Properties"
        selectedPropertyId={selectedPropertyId}
        onPropertySelect={handlePropertySelect}
        enableNavigateToPortfolio={true}
        isPropertyCategory={showCategory}
        propertyCategoryName={propertyCategoryName}
        tailLabel={tailLabel}
      />

      {!showCategory && (
        // Basis is the collapsed width (range dropdown + search), so the pills fold into the
        // dropdown before this group wraps under the property name.
        <div className="flex min-w-0 flex-1 basis-[25rem] items-center justify-end gap-5">
          <DashboardTimeRangePills
            className="flex-1"
            timeRange={timeRange}
            unavailableTimeRanges={unavailableTimeRanges}
            onTimeRangeChange={(value) => onTimeRangeChange?.(value)}
          />
          <div className="h-[44px] shrink-0 text-xl flex items-center bg-white text-zeos-grey-md border-1 border-gray-400 rounded-[4px] ">
            <input
              className="border-none outline-none pl-3"
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(event) => onSearchChange?.(event.target.value)}
            />
            <div className="h-4/5 w-[1.5px] bg-zeos-grey-md"></div>
            <div className="h-full flex justify-center items-center px-2">
              <IoSearchSharp size={24} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
